import React, { useState } from 'react'
import { Icon } from '@chakra-ui/icons'
import { Avatar } from '@chakra-ui/react'
import { Button } from '@chakra-ui/react'
import { FaMagnifyingGlass } from 'react-icons/fa6'
import { Box, Flex, Text, Center } from '@chakra-ui/react'
import { Input, InputGroup, InputLeftElement } from '@chakra-ui/react'

import {
    UserFieldsFragment,
    useQueryClinicAdminsQuery,
    InternalChatFieldsFragment,
    useQueryClinicGroupUsersQuery,
} from '~graphql/generated/graphql'
import { getUserFullName } from '~utils/helpers'
import { useTeamChatConversation } from '../../provider'
import { iLikeStringFilter } from '~utils/query-helpers'
import SharedLabel from '~components/shared/form/shared-label'
import SharedStack from '~components/shared/wrappers/shared-stack'
import AsyncWrapper from '~components/shared/wrappers/async-wrapper'
import InternalLink from '~components/shared/texts/internal-link'
import { UserRoutes } from '~config/routes'
import InternalChatMemberDetails from './member-details'

export default function TeamChatMembersEdit() {
    const { internalChat, currentInternalChatUser, internalChatUsers } =
        useTeamChatConversation()

    const [query, setQuery] = useState('')

    const { users, loading } = useUsersQuery(query, internalChat)

    return (
        <Box>
            {/* <SharedLabel mb={2}>Members</SharedLabel> */}
            <Box>
                <InputGroup variant="filled" size="sm">
                    <InputLeftElement h="42px">
                        <Icon as={FaMagnifyingGlass} color="gray.500" />
                    </InputLeftElement>
                    <Input
                        h="42px"
                        borderWidth={1}
                        borderColor="gray.300"
                        rounded="lg"
                        px={6}
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        placeholder="Search clinic users..."
                    />
                </InputGroup>
                {query ? (
                    <AsyncWrapper
                        loading={loading}
                        empty={users.length === 0}
                        emptyComponent={<EmptyUsersComponent query={query} />}
                    >
                        <SharedStack mt={4} spacing={2}>
                            {users.map(user => (
                                <InternalChatMemberDetails
                                    key={user.id}
                                    user={user}
                                    internalChatUserId={
                                        [
                                            currentInternalChatUser,
                                            ...internalChatUsers,
                                        ]
                                            .filter(u => !!u)
                                            .find(u => u.user.id === user.id)
                                            ?.id
                                    }
                                />
                            ))}
                        </SharedStack>
                    </AsyncWrapper>
                ) : (
                    <SharedStack mt={4} spacing={2}>
                        {[currentInternalChatUser, ...internalChatUsers]
                            .filter(u => !!u)
                            .map(internalChatUser => (
                                <InternalChatMemberDetails
                                    key={internalChatUser.user.id}
                                    user={internalChatUser.user}
                                    internalChatUserId={internalChatUser.id}
                                />
                            ))}
                    </SharedStack>
                )}
            </Box>
        </Box>
    )
}

function useUsersQuery(
    query: string,
    internalChat: InternalChatFieldsFragment,
) {
    const { data: clinicAdminsData, loading: clinicAdminsLoading } =
        useQueryClinicAdminsQuery({
            variables: {
                clinicId: internalChat.clinic?.id || '',
                where: [iLikeStringFilter('user.first_name', query)],
                page: 0,
                pageSize: 10,
            },
            skip: !internalChat.clinic?.id || !query,
        })

    const { data: clinicGroupUsersData, loading: clinicGroupUsersLoading } =
        useQueryClinicGroupUsersQuery({
            variables: {
                clinicGroupId: internalChat.clinicGroup?.id || '',
                where: [iLikeStringFilter('user.first_name', query)],
                page: 0,
                pageSize: 10,
            },
            skip: !internalChat.clinicGroup?.id || !query,
        })

    const users = clinicAdminsData?.queryClinicAdmins?.length
        ? clinicAdminsData.queryClinicAdmins.map(u => u.user)
        : clinicGroupUsersData?.queryClinicGroupUsers?.length
          ? clinicGroupUsersData.queryClinicGroupUsers.map(u => u.user)
          : []
    const loading = clinicAdminsLoading || clinicGroupUsersLoading

    return {
        users,
        loading,
    }
}

function EmptyUsersComponent({ query }: { query: string }) {
    return (
        <Center
            flexDir="column"
            gridGap={1}
            bg="gray.100"
            p={6}
            rounded="lg"
            mt={4}
        >
            <Text fontSize="md" fontWeight="semibold">
                No matches found for {query}
            </Text>
            <Text fontSize="xs" color="gray.600">
                Try changing your search term or add new users from the{' '}
                <InternalLink to={UserRoutes.ClinicUsers}>
                    Users Page
                </InternalLink>
            </Text>
        </Center>
    )
}
